import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../components/Input';
import TextArea from '../../../../../components/TextArea';
import Switch from '../../../../../components/Switch';
import Dropdown from '../../../../../components/Dropdown';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { Validation } from '../../../../../interfaces/common';
import { AddEdit as AddEditInterface } from '../../../../../interfaces/product.interface';

interface ProductDetailsTabProps {
  edit: boolean;
  view?: boolean;
  formData: AddEditInterface;
  validation: Validation;
  divisionOptions: { label: string; value: string }[];
  allDivisionOptions: { label: string; value: string }[];
  divisionSearchText: string;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  onDivisionSearch: (e: any, name: string) => void;
}

const ProductDetailsTab: React.FC<ProductDetailsTabProps> = ({
  edit,
  view,
  formData,
  validation,
  divisionOptions,
  allDivisionOptions,
  divisionSearchText,
  onKeyDown,
  onInputBlur,
  onInputChange,
  onDivisionSearch,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">1. {t('products.addEdit.pageTexts.form.sections.primaryDetails')}</h6>

        {/* Name */}
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.name}
              name={VALIDATION_FIELD_MAPPING.name}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.name)}
              errorMessage={
                validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.name)
                  ?.message ?? ''
              }
              label={t('products.addEdit.pageTexts.form.name.label')}
              placeholder={t('products.addEdit.pageTexts.form.name.placeholder')}
              value={formData.name}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.name)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.name)}
            />
          </div>
        </div>

        {/* Division Dropdown */}
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.product_division_id && formData.product_division_id.toString().trim()
                  ? [String(formData.product_division_id)]
                  : []
              }
              options={divisionOptions}
              allOptions={allDivisionOptions}
              id={VALIDATION_FIELD_MAPPING.product_division_id}
              name={VALIDATION_FIELD_MAPPING.product_division_id}
              searchText={divisionSearchText}
              onSearch={onDivisionSearch}
              placeholder={t('products.addEdit.pageTexts.form.division.placeholder')}
              label={t('products.addEdit.pageTexts.form.division.label')}
              required
              disabled={view}
              error={!!validation.fields.find((f) => f.field === VALIDATION_FIELD_MAPPING.product_division_id)}
              errorMessage={
                validation.fields.find((f) => f.field === VALIDATION_FIELD_MAPPING.product_division_id)?.message ?? ''
              }
              onChange={(e: any) => {
                const value = Array.isArray(e.target?.value)
                  ? e.target.value[0]
                  : e.target?.value || '';
                onInputChange({ target: { value } }, VALIDATION_FIELD_MAPPING.product_division_id);
              }}
            />
          </div>
        </div>

        <h6 className="mb-0 mt-4">2. {t('products.addEdit.pageTexts.form.sections.otherInformation')}</h6>

        {/* Description */}
        <div className="col-md-10">
          <div className="form-floating form-floating-outline">
            <TextArea
              required
              className="form-control-lg h-100"
              id={VALIDATION_FIELD_MAPPING.description}
              name={VALIDATION_FIELD_MAPPING.description}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.description
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.description
                )?.message ?? ''
              }
              label={t('products.addEdit.pageTexts.form.description.label')}
              placeholder={t('products.addEdit.pageTexts.form.description.placeholder')}
              value={formData.description}
              rows={5}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.description)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.description)}
            />
          </div>
        </div>

        {/* Status */}
        <div className="col-md-2">
          <Switch
            label={t('products.addEdit.pageTexts.form.status.label')}
            name={VALIDATION_FIELD_MAPPING.status}
            checked={formData.status === 'true'}
            disabled={view}
            onChange={(e: any, name: string) => {
              const value = e.target.checked ? 'true' : 'false';
              const fakeEvent = { target: { value } };
              onInputChange(fakeEvent, name);
            }}
          />
        </div>
      </div>
    </form>
  );
};

export default ProductDetailsTab;
